"use client";

import { useLocale } from "next-intl";
import {
  type ComponentProps,
  type MouseEvent,
  useCallback,
  useMemo,
} from "react";

import { navigateWithTransition } from "@/lib/view-transitions/page-transition";

import { getPathname, IntlLink, useIntlRouter } from "./navigation-base";

// =============================================================================

type IntlRouter = ReturnType<typeof useIntlRouter>;
type Href = Parameters<IntlRouter["push"]>[0];
type NavigateOptions = Parameters<IntlRouter["push"]>[1];

// Same rules as next/link: let the browser handle new tabs, downloads…
function shouldPreserveDefault(event: MouseEvent<HTMLAnchorElement>): boolean {
  const target = event.currentTarget.getAttribute("target");
  return Boolean(
    (target && target !== "_self") ||
    event.currentTarget.hasAttribute("download") ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.button !== 0
  );
}

// =============================================================================

/**
 * next-intl router whose push and replace run inside the page transition.
 */
export function useRouter(): IntlRouter {
  const router = useIntlRouter();
  const currentLocale = useLocale();

  const withTransition = useCallback(
    (
      navigate: IntlRouter["push"],
      href: Href,
      options: NavigateOptions | undefined
    ) => {
      const locale = options?.locale ?? currentLocale;
      navigateWithTransition(
        { href: getPathname({ href, locale }), locale },
        () => navigate(href, options)
      );
    },
    [currentLocale]
  );

  const push = useCallback(
    (href: Href, options?: NavigateOptions) =>
      withTransition(router.push, href, options),
    [router, withTransition]
  );

  const replace = useCallback(
    (href: Href, options?: NavigateOptions) =>
      withTransition(router.replace, href, options),
    [router, withTransition]
  );

  return useMemo(() => ({ ...router, push, replace }), [router, push, replace]);
}

// =============================================================================

export type LinkProps = ComponentProps<typeof IntlLink>;

/**
 * Locale-aware link that navigates with the page transition. Links to the
 * current page (anchors, filters) keep the default behavior.
 */
export function Link({
  href,
  locale,
  replace,
  scroll,
  onClick,
  ...props
}: LinkProps) {
  const router = useRouter();

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      if (event.defaultPrevented || shouldPreserveDefault(event)) return;
      if (typeof href !== "string") return;
      const url = new URL(event.currentTarget.href, window.location.href);
      if (
        url.origin !== window.location.origin ||
        url.pathname === window.location.pathname
      ) {
        return;
      }

      event.preventDefault();
      const navigate = replace ? router.replace : router.push;
      navigate(href, { locale, scroll: scroll ?? true });
    },
    [onClick, href, locale, replace, scroll, router]
  );

  return (
    <IntlLink
      href={href}
      locale={locale}
      replace={replace}
      scroll={scroll}
      onClick={handleClick}
      {...props}
    />
  );
}

// =============================================================================
